import React, {useEffect, useState} from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import Button from '@salesforce/design-system-react/components/button';
import DefaultAnnouncements from 'announcements';
import DefaultTaskEngine from 'tasks/task-engine';
import { IPastRun } from '../../interfaces/IHistory.interface';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import { getModuleLabel, isRefreshModule, isUpdatesModule, isUpgradesModule, isFreshModule, getIconVariantFromTaskStatus, formatTaskDuration, formatTotalTaskDuration } from 'common/module.utils';

const TASK_FRESHNESS_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

interface ICQTimelineProps {
    setIsEmptyEvent: React.Dispatch<React.SetStateAction<boolean>>;
    currentModule: string;
}

export function CQTimeline(props: ICQTimelineProps) {
    const {t} = useTranslation();
    const [events, setEvents] = useState<any>([]);

    function initializeData() {
        const announcements = DefaultAnnouncements.announcements;
        DefaultTaskEngine.getHistory().then((history) => {
            let eventUpdates : any =  history?.filter(task => isUpdatesModule(task?.moduleId));
            let eventUpgrades: any = history?.filter(task => isUpgradesModule(task?.moduleId));
            let eventRefreshes : any = history?.filter(task => isRefreshModule(task?.moduleId));
            let eventFresh : any = history?.filter(task => isFreshModule(task?.moduleId));
            
            eventUpdates = eventUpdates.concat(announcements);
            eventUpgrades = eventUpgrades.concat(announcements);
            eventRefreshes = eventRefreshes.concat(announcements);
            eventFresh = eventFresh.concat(announcements);

            // adds only last 3 runs in the timeline
            const latestEventUpdates = getLatestThreeRuns(eventUpdates);
            const latestEventUpgrades = getLatestThreeRuns(eventUpgrades);
            const latestEventRefreshes = getLatestThreeRuns(eventRefreshes);
            const latestEventFresh = getLatestThreeRuns(eventFresh);

            let selectedEvents;
            if (isUpdatesModule(props.currentModule)) {
                selectedEvents = latestEventUpdates;
            } else if (isRefreshModule(props.currentModule)) {
                selectedEvents = latestEventRefreshes;
            } else if (isFreshModule(props.currentModule)) {
                selectedEvents = latestEventFresh;
            } else {
                selectedEvents = latestEventUpgrades;
            }
            setEvents(selectedEvents);
            props.setIsEmptyEvent(selectedEvents.length === 0);
        });
    }

    /** 
     * slices array to 3 items and sorts them in descending order based on their date
     * @param evt - array of object of events
     * @returns - sliced and sorted array of objects of events
     */
    function getLatestThreeRuns(evt : any) {
        evt = evt.slice(0, 3);
        evt.sort((a: any,b: any) => {
            let date1 = new Date(a.startedOn || a.releasedOn);
            let date2 = new Date(b.startedOn || b.releasedOn);
            
            return date1 > date2 ? -1 : date1 === date2 ? 0 : 1;
        });
        
        return evt;
    }

    useEffect(() => {
        initializeData();
        const historyChangeListener = () => {
            initializeData();
        };
        DefaultTaskEngine.addListener(historyChangeListener);

        return () => {
            DefaultTaskEngine.removeListener(historyChangeListener)
        }
    }, [props.currentModule]);

    function getEventComponent(evt: any) {
        if(evt.releasedOn) {
            return getAnnoucement(evt);
        } else {
            return getTask(evt);
        }
    }

    function getAnnoucement(evt: any) {
        let key = evt.name;
        let releasedOn = new Date(evt.releasedOn).toLocaleDateString();

        return <>
            <li key={key}>
            <div className="slds-timeline__item_expandable slds-timeline__item_event slds-is-open">
                    <span className="slds-assistive-text">task</span>
                    <div className="slds-media">
                        <div className="slds-media__figure">
                            <button className="slds-button slds-button_icon" title="" aria-controls="task-item-expanded-65" aria-expanded="true">
                                <Icon category="utility" name="switch" size="xx-small"></Icon>
                                <span className="slds-assistive-text">{evt.name}</span>
                            </button>
                            <Icon category="custom" name="custom53" size="small"></Icon>
                        </div>
                        <div className="slds-media__body">
                            <div className="slds-grid slds-grid_align-spread slds-timeline__trigger">
                                <div className="slds-grid slds-grid_vertical-align-center slds-truncate_container_75 slds-no-space">
                                    
                                    <h3 className="slds-truncate" title={evt.name}>
                                        <a href="#">
                                            <strong>{evt.name}</strong>
                                        </a>
                                        <span className="slds-timeline__date slds-m-left_x-small">{t("timeline_announced_on", {date: releasedOn})}</span>
                                    </h3>
                                    <div className="slds-no-flex">
                                        <span className="slds-icon_container slds-icon-utility-rotate" title="Recurring Task">
                                            <span className="slds-assistive-text">Recurring Task</span>
                                        </span>
                                    </div>
                                </div>
                                <div className="slds-timeline__actions slds-timeline__actions_inline">
                                </div>
                            </div>
                            <article className="slds-box slds-timeline__item_details slds-m-top_x-small slds-m-horizontal_xx-small slds-p-around_medium" id="task-item-expanded-65" aria-hidden="false">
                                <div>
                                    {/* <span className="slds-text-title">Description</span> */}
                                    <p className="slds-p-top_x-small" dangerouslySetInnerHTML={{__html: evt.description}}></p>
                                </div>
                            </article>
                        </div>
                    </div>
                </div>
            </li>
        </>
    }

    function getTask(evt: any) {
        const task : IPastRun = evt;
        let date = new Date(evt.startedOn);
        let startedOn = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        let key = evt.moduleId + '-' + startedOn;
        let moduleName = getModuleLabel(task.forModule!);
        let isFresh = (new Date().getTime() - date.getTime()) < TASK_FRESHNESS_THRESHOLD;
        const totalDuration = formatTotalTaskDuration(task.tasks);
        return <>
            <li key={key}>
                <div className="slds-timeline__item_expandable slds-timeline__item_task slds-is-open">
                    <span className="slds-assistive-text">task</span>
                    <div className="slds-media" >
                        <div className="slds-media__figure">
                            <button className="slds-button slds-button_icon" title="" aria-controls="task-item-expanded-65" aria-expanded="true">
                                <Icon category="utility" name="switch" size="xx-small"></Icon>
                                <span className="slds-assistive-text"></span>
                            </button>
                            <Icon category="standard" name="task" size="small"></Icon>
                        </div>
                        <div className="slds-media__body" style={{backgroundColor: isFresh ? "#ffe0b2": "initial"}}>
                            <div className="slds-grid slds-grid_align-spread slds-timeline__trigger" >
                                <div className="slds-grid slds-grid_vertical-align-center slds-truncate_container_75 slds-no-space">
                                    
                                    <h3 className="slds-truncate" title="">
                                        <a href="#">
                                            <strong>{task.versionName ? task.versionName : moduleName}</strong>
                                        </a>
                                        <br/>
                                        <span className="slds-timeline__date slds-m-left_x-small">{t("timeline_ran_on", {date: startedOn})}</span>
                                    </h3>
                                    <div className="slds-no-flex">
                                        <span className="slds-icon_container slds-icon-utility-rotate" title="">
                                            <span className="slds-assistive-text"></span>
                                        </span>
                                    </div>
                                </div>
                                <div className="slds-timeline__actions slds-timeline__actions_inline">
                                    <Button variant="base" label={t("Download")} iconName="download" iconCategory="utility"
                                     iconPosition="left" className="slds-m-right_x-small slds-hide"></Button>
                                </div>
                            </div>
                            <article className="slds-box slds-timeline__item_details slds-m-top_x-small slds-m-horizontal_xx-small slds-p-around_medium" id="task-item-expanded-65" aria-hidden="false" style={{borderColor: isFresh ? 'gray' : 'rgb(229, 229, 229)'}}>
                                <ul>
                                    {task.tasks.map((tsk) => {
                                        const iconConfig = getIconVariantFromTaskStatus(tsk.isSkipped, tsk.status, tsk.wasPreviouslyCompleted);
                                        const duration = formatTaskDuration(tsk.startedOn, tsk.endedOn);
                                        return <li key={tsk.name} className="slds-item slds-grid slds-grid_vertical-align-top ">
                                            <Icon category="utility" name={iconConfig.icon} colorVariant={iconConfig.colorVariant} size="xx-small" className="slds-m-right_x-small "></Icon>
                                            <span>
                                                {tsk.isSkipped ?
                                                    <span className='slds-text-color_inverse-weak'>{(i18n.exists('task_' + tsk.name) ? t('task_' + tsk.name) : tsk.name)}</span> :
                                                    <span>{tsk.label ? tsk.label : (i18n.exists('task_' + tsk.name) ? t('task_' + tsk.name) : tsk.name)}</span>
                                                }
                                                {duration && <span className="slds-text-color_weak slds-m-left_xx-small">( {duration} ) </span>}
                                            </span>
                                        </li>
                                    })}
                                </ul>
                                {totalDuration && <div className="slds-m-top_small slds-text-title_bold">{t("total_time_taken")}: {totalDuration}</div>}
                            </article>
                            {isFresh ? <div className="slds-m-top_x-small"></div>: <></>}
                        </div>
                    </div>
                </div>
            </li>
        </>
    }
    
    return <>
        <ul className="slds-timeline slds-is-relative">
            {events.map((evt: any) => getEventComponent(evt))}
        </ul>
    </>
}